import { configureStore as toolkitConfigureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { DataCapsule, LocalStorageStrategy } from 'data-capsule';
import { createLogger } from 'redux-logger';

import { PersistConfig, persistReducer, persistStore } from 'redux-persist';
import reduxThunk from 'redux-thunk';
import { getWizardSessionId } from '../lib/wizardSession';

import rootReducer from './rootReducer';

// NOTE: LocalStorageStrategy automatically manages expiration and auto-cleans to never exceed quota.
// https://github.com/wix/data-capsule
const capsule = new DataCapsule({
  strategy: new LocalStorageStrategy(),
  namespace: 'vmaf-1',
  scope: getWizardSessionId(),
});

// NOTE: if need version + migration reed:
// https://github.com/rt2zz/redux-persist#migrations
const DEFAULT_PERSIST_CONFIG: PersistConfig = {
  key: 'wizard',
  storage: capsule,
  // NOTE: dont set throttle! We have to be sure latest state is recovered after reload
  // throttle: 1000,
};

export default function configureStore(
  persistConfig: Pick<PersistConfig, 'blacklist'> = {},
) {
  const _persistConfig = {
    ...DEFAULT_PERSIST_CONFIG,
    ...persistConfig,
  };

  const middlewares = [reduxThunk];
  const wrappedReducer = persistReducer(_persistConfig, rootReducer);

  if (process.env.NODE_ENV === 'development') {
    middlewares.push(createLogger({ collapsed: true, diff: true }));
  }

  const store = toolkitConfigureStore({
    reducer: wrappedReducer,
    middleware: [...middlewares],
  });

  if (module.hot) {
    module.hot.accept('./rootReducer', () => {
      const nextReducer = require('./rootReducer').default;
      store.replaceReducer(persistReducer(_persistConfig, nextReducer));
    });
  }
  setupListeners(store.dispatch);
  const persistor = persistStore(store);

  return { store, persistor };
}
